import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import moment from "moment";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import MobileTopbar from "./MobileTopbar";
import MobileTabBar from "./MobileTabBar";
import MoodFace from "./MoodFace";
import { useAgentInsights } from "./useAgentInsights";
import { useContactContext } from "../../../contexts/ContactContext";

const MOOD_OPTIONS = [
  { value: 1, color: "#e34948", labelKey: "moodVeryBad" },
  { value: 2, color: "#eb6834", labelKey: "moodBad" },
  { value: 3, color: "#eda100", labelKey: "moodNeutral" },
  { value: 4, color: "#639922", labelKey: "moodGood" },
  { value: 5, color: "#1baf7a", labelKey: "moodVeryGood" },
];

/**
 * Mobile "Inicio" screen - same data and same useAgentInsights hook as
 * desktop's AgentDashborad.jsx, same MoodFace component (not a
 * simplified stand-in), and the same bottom tab bar used on the call
 * screens so the agent can navigate away from here - only the layout
 * (single column, compact cards) is mobile-specific.
 */
const MobileHomeScreen = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const agentName = sessionStorage.getItem("userName");
  const companyName = sessionStorage.getItem("clientName");
  const { coldCallContacts, followUpContacts, meetingContacts, meetingRecovery } = useContactContext();

  const {
    period,
    moodStatus,
    moodSubmitting,
    getHighestPriorityCallType,
    handleMonthChange,
    handleMoodClick,
    kpisByDay,
    meetingsByDay,
    answeredPct,
    notAnsweredPct,
    ranking,
    rankBarPct,
  } = useAgentInsights();

  const handleStartCalls = () => {
    const callType = getHighestPriorityCallType();
    if (callType) {
      navigate(`/agent/workspace/${callType}`);
    }
  };

  const counts = {
    "cold-calls": coldCallContacts.length,
    "follow-up": followUpContacts.length,
    meeting: meetingContacts.length,
    "meeting-recovery": meetingRecovery.length,
  };

  return (
    <div className="flex flex-col h-screen bg-secondaryBg">
      <MobileTopbar agentName={agentName} companyName={companyName} />

      <div className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-3">
        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => handleMonthChange(-1)}
            className="w-7 h-7 flex items-center justify-center rounded-md border border-gray-200 bg-white"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0A1E4C" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <span className="text-sm font-bold text-primary capitalize">
            {moment({ year: period.year, month: period.month - 1 }).format("MMMM YYYY")}
          </span>
          <button
            type="button"
            onClick={() => handleMonthChange(1)}
            className="w-7 h-7 flex items-center justify-center rounded-md border border-gray-200 bg-white"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#0A1E4C" strokeWidth="2">
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-3.5">
          <p className="text-[11px] text-gray-500 mb-1.5">
            {t("agent.agentDashboard.insights.positionTitle")}
          </p>
          <p className="text-base font-bold text-primary mb-2">
            {ranking && ranking.my_position
              ? t("agent.agentDashboard.insights.positionValue", {
                  position: ranking.my_position,
                  total: ranking.total_agents,
                })
              : "\u2014"}
          </p>
          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-secondary" style={{ width: `${rankBarPct}%` }} />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-3.5">
          <p className="text-[11px] text-gray-500 mb-1.5">
            {t("agent.agentDashboard.insights.answeredTitle")}
          </p>
          <div className="flex justify-between text-xs mb-1.5">
            <span>
              <span className="font-bold">{answeredPct}%</span>{" "}
              <span className="text-gray-400">{t("agent.agentDashboard.insights.answeredYes")}</span>
            </span>
            <span>
              <span className="font-bold">{notAnsweredPct}%</span>{" "}
              <span className="text-gray-400">{t("agent.agentDashboard.insights.answeredNo")}</span>
            </span>
          </div>
          <div className="w-full h-1.5 rounded-full overflow-hidden flex">
            <div className="h-full bg-secondary" style={{ width: `${answeredPct}%` }} />
            <div className="h-full bg-gray-300" style={{ width: `${notAnsweredPct}%` }} />
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-3.5">
          <p className="text-[11px] text-gray-500 mb-2">
            {t("agent.agentDashboard.insights.moodTitle")}
          </p>
          <div className="flex justify-between gap-1">
            {MOOD_OPTIONS.map((m) => (
              <button
                key={m.value}
                type="button"
                onClick={() => handleMoodClick(m.value)}
                disabled={moodStatus.already_submitted || moodSubmitting}
                className="flex-1 flex flex-col items-center justify-center gap-1 py-1"
              >
                <MoodFace
                  value={m.value}
                  color={moodStatus.already_submitted ? "#9ca3af" : m.color}
                  size={30}
                />
                <span className="text-[8px] text-gray-400 leading-tight text-center">
                  {t(`agent.agentDashboard.insights.${m.labelKey}`)}
                </span>
              </button>
            ))}
          </div>
          <p className="text-[9px] text-gray-400 mt-2 text-center">
            {moodStatus.already_submitted
              ? moodStatus.mood_value <= 2
                ? t("agent.agentDashboard.insights.moodDoneEmpathy")
                : t("agent.agentDashboard.insights.moodDonePositive")
              : t("agent.agentDashboard.insights.moodOncePerDay")}
          </p>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-3.5">
          <p className="text-[11px] text-gray-500 mb-2">
            {t("agent.agentDashboard.insights.kpiChartTitle")}
          </p>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={kpisByDay}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
              <XAxis dataKey="day" tick={{ fontSize: 8 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 8 }} axisLine={false} tickLine={false} />
              <Tooltip />
              <Bar dataKey="realizadas" stackId="a" fill="#136BF0" />
              <Bar dataKey="contestadas" stackId="a" fill="#1baf7a" />
              <Bar dataKey="interesados" stackId="a" fill="#eda100" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-3.5">
          <p className="text-[11px] text-gray-500 mb-2">
            {t("agent.agentDashboard.insights.meetingsChartTitle")}
          </p>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={meetingsByDay}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
              <XAxis dataKey="day" tick={{ fontSize: 8 }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 8 }} axisLine={false} tickLine={false} />
              <Tooltip />
              <Bar dataKey="agendadas" stackId="b" fill="#136BF0" />
              <Bar dataKey="efectivas" stackId="b" fill="#1baf7a" radius={[3, 3, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <button
          type="button"
          onClick={handleStartCalls}
          className="bg-secondary text-white text-sm font-extrabold rounded-xl py-3 mt-1"
        >
          {t("agent.agentDashboard.insights.ctaLabel")}
        </button>
      </div>

      <MobileTabBar activeCallType={null} counts={counts} />
    </div>
  );
};

export default MobileHomeScreen;
